import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, Package, ShoppingBag, Users, BarChart3, Settings, ClipboardList,
  Upload, RefreshCw, Trash2, Download, User, Coins, Clock, AlertTriangle
} from 'lucide-react';
import { API_URL } from '../config/api';
import logService from '../services/logService';
import useEscapeClose from '../hooks/useEscapeClose';
import './TrendyolSettings.css';

const ORDERS_POLL_INTERVAL = 30000;
const PRODUCTS_POLL_INTERVAL = 60000;

const TrendyolSettings = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const isAdmin = user?.role === 'admin';

  // Auto-sync state
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [lastOrderSync, setLastOrderSync] = useState(null);
  const [lastProductSync, setLastProductSync] = useState(null);
  const [isLive, setIsLive] = useState(true);
  const ordersIntervalRef = useRef(null);
  const productsIntervalRef = useRef(null);

  // Settings state
  const [settings, setSettings] = useState({
    seller_id: '',
    api_key: '',
    api_secret: '',
    integration_code: '',
    default_deduction_store_id: '',
    sync_enabled: false,
    commission_percentage: 0,
    price_change_threshold: 2,
    price_sync_interval: 0,
    last_synced_gold_price: null,
    last_synced_usd_rate: null,
    last_price_sync_at: null
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Stores
  const [stores, setStores] = useState([]);

  // Products state
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [syncingInventory, setSyncingInventory] = useState(false);
  const [syncingPrices, setSyncingPrices] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [productFilter, setProductFilter] = useState('');
  const [imagePopup, setImagePopup] = useState({ show: false, src: '', name: '' });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [pullingOrders, setPullingOrders] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all');
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkDeletingOrders, setBulkDeletingOrders] = useState(false);

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');

  // Logs state
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logFilter, setLogFilter] = useState({ type: '', status: '' });

  // Confirm modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '', message: '', confirmText: 'Evet', cancelText: 'İptal', type: 'warning', onConfirm: () => {}
  });

  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [imagePopup.show, () => setImagePopup({ show: false, src: '', name: '' })],
  ]);

  const tabs = [
    { id: 'products', name: 'Ürünler', icon: <Package size={18} /> },
    { id: 'orders', name: 'Siparişler', icon: <ShoppingBag size={18} /> },
    { id: 'customers', name: 'Müşteriler', icon: <Users size={18} /> },
    { id: 'settings', name: 'Ayarlar', icon: <Settings size={18} /> },
    { id: 'logs', name: 'Sync Logları', icon: <ClipboardList size={18} /> }
  ];

  // Fetch stores
  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/stores`);
      const data = await response.json();
      setStores(data);
    } catch (error) {
      console.error('Mağazalar yüklenemedi:', error);
    }
  };

  // Fetch settings
  const fetchSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/settings`);
      const data = await response.json();
      if (data.settings) {
        setSettings({
          seller_id: data.settings.seller_id || '',
          api_key: data.settings.api_key || '',
          api_secret: data.settings.api_secret || '',
          integration_code: data.settings.integration_code || '',
          default_deduction_store_id: data.settings.default_deduction_store_id || '',
          sync_enabled: data.settings.sync_enabled === 1,
          commission_percentage: data.settings.commission_percentage || 0,
          price_change_threshold: data.settings.price_change_threshold || 2,
          price_sync_interval: data.settings.price_sync_interval || 0,
          last_synced_gold_price: data.settings.last_synced_gold_price,
          last_synced_usd_rate: data.settings.last_synced_usd_rate,
          last_price_sync_at: data.settings.last_price_sync_at
        });
      }
    } catch (error) {
      console.error('Trendyol ayarları yüklenemedi:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    if (!settings.seller_id || !settings.api_key || !settings.api_secret) {
      toast.error('Satıcı ID, API Key ve API Secret zorunludur');
      return;
    }

    setSavingSettings(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Trendyol ayarları kaydedildi');
        logService.log('trendyol', 'update', 'Trendyol ayarları güncellendi', { status: 'success' });
      } else {
        const data = await response.json();
        toast.error(data.error || 'Ayarlar kaydedilemedi');
      }
    } catch (error) {
      console.error('Ayar kaydetme hatası:', error);
      toast.error('Ayarlar kaydedilemedi');
    } finally {
      setSavingSettings(false);
    }
  };

  // Test connection
  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await fetch(`${API_URL}/trendyol/test-connection`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        setConnectionStatus({ success: true, message: data.message });
        toast.success('Trendyol bağlantısı başarılı!');
      } else {
        setConnectionStatus({ success: false, message: data.error });
        toast.error(data.error || 'Bağlantı başarısız');
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: error.message });
      toast.error('Bağlantı test edilemedi');
    } finally {
      setTestingConnection(false);
    }
  };

  // Disconnect
  const disconnect = async () => {
    try {
      await fetch(`${API_URL}/trendyol/auth/disconnect`, { method: 'POST' });
      setSettings(prev => ({ ...prev, api_key: '', api_secret: '', sync_enabled: false }));
      toast.success('Trendyol bağlantısı kesildi');
    } catch (error) {
      toast.error('Bağlantı kesilemedi');
    }
  };

  // ===== PRODUCTS =====
  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/products?page=1&limit=500`);
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const syncAllProducts = async () => {
    setSyncingProducts(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/products/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        // Arka plan sync durumunu takip et
        if (data.backgroundSync) {
          pollSyncStatus('products');
        }
      } else {
        toast.error(data.error || 'Sync başarısız');
        setSyncingProducts(false);
      }
    } catch (error) {
      toast.error('Sync işlemi başarısız');
      setSyncingProducts(false);
    }
  };

  const syncSingleProduct = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/trendyol/products/${productId}/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success('Ürün sync edildi');
        fetchProducts();
      } else {
        toast.error(data.error || 'Sync başarısız');
      }
    } catch (error) {
      toast.error('Sync işlemi başarısız');
    }
  };

  const deleteProduct = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/trendyol/products/${productId}/sync`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        toast.success('Ürün Trendyol\'dan kaldırıldı');
        fetchProducts();
      } else {
        toast.error(data.error || 'Silme başarısız');
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const bulkDeleteProducts = async () => {
    if (selectedProducts.length === 0) return;
    setBulkDeleting(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/products/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds: selectedProducts })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setSelectedProducts([]);
        fetchProducts();
      } else {
        toast.error(data.error || 'Toplu silme başarısız');
      }
    } catch (error) {
      toast.error('Toplu silme başarısız');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Sync inventory
  const syncInventory = async () => {
    setSyncingInventory(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/inventory/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Stok senkronizasyonu başlatıldı');
        if (data.backgroundSync) {
          pollSyncStatus('inventory');
        }
      } else {
        toast.error(data.error || 'Stok sync başarısız');
        setSyncingInventory(false);
      }
    } catch (error) {
      toast.error('Stok sync başarısız');
      setSyncingInventory(false);
    }
  };

  // Sync prices
  const syncPrices = async () => {
    setSyncingPrices(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/prices/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Fiyat senkronizasyonu başlatıldı');
        if (data.backgroundSync) {
          pollSyncStatus('prices');
        }
      } else {
        toast.error(data.error || 'Fiyat sync başarısız');
        setSyncingPrices(false);
      }
    } catch (error) {
      toast.error('Fiyat sync başarısız');
      setSyncingPrices(false);
    }
  };

  // Poll sync status
  const pollSyncStatus = (type) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/trendyol/${type}/sync-status`);
        const data = await response.json();
        if (data.syncStatus) {
          setSyncProgress(data.syncStatus);
          if (!data.syncStatus.running) {
            clearInterval(interval);
            setSyncProgress(null);
            if (type === 'products') {
              setSyncingProducts(false);
              fetchProducts();
            } else if (type === 'inventory') {
              setSyncingInventory(false);
            } else if (type === 'prices') {
              setSyncingPrices(false);
            }
            toast.success(`${type === 'products' ? 'Ürün' : type === 'prices' ? 'Fiyat' : 'Stok'} sync tamamlandı: ${data.syncStatus.success} başarılı, ${data.syncStatus.failed} hata`);
          }
        }
      } catch (error) {
        clearInterval(interval);
      }
    }, 3000);
  };

  // ===== ORDERS =====
  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const statusParam = orderFilter !== 'all' ? `&status=${orderFilter}` : '';
      const response = await fetch(`${API_URL}/trendyol/orders?page=1&limit=100${statusParam}`);
      const data = await response.json();
      setOrders(data.orders || []);
      const pendingCount = (data.orders || []).filter(o => o.sync_status === 'pending').length;
      setNewOrderCount(pendingCount);
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
    } finally {
      setLoadingOrders(false);
    }
  }, [orderFilter]);

  const pullOrders = async () => {
    setPullingOrders(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/orders/pull`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchOrders();
      } else {
        toast.error(data.error || 'Sipariş çekme başarısız');
      }
    } catch (error) {
      toast.error('Sipariş çekme başarısız');
    } finally {
      setPullingOrders(false);
    }
  };

  const processOrder = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/trendyol/orders/${orderId}/process`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success('Sipariş işlendi');
        fetchOrders();
      } else {
        toast.error(data.error || 'İşleme başarısız');
      }
    } catch (error) {
      toast.error('İşleme başarısız');
    }
  };

  const bulkProcessOrders = async () => {
    setBulkProcessing(true);
    try {
      const body = selectedOrders.length > 0
        ? { orderIds: selectedOrders }
        : { processAll: true };
      const response = await fetch(`${API_URL}/trendyol/orders/bulk-process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setSelectedOrders([]);
        fetchOrders();
      } else {
        toast.error(data.error || 'Toplu işleme başarısız');
      }
    } catch (error) {
      toast.error('Toplu işleme başarısız');
    } finally {
      setBulkProcessing(false);
    }
  };

  const bulkDeleteOrders = async () => {
    setBulkDeletingOrders(true);
    try {
      const body = selectedOrders.length > 0
        ? { orderIds: selectedOrders }
        : { deleteAll: true };
      const response = await fetch(`${API_URL}/trendyol/orders/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setSelectedOrders([]);
        fetchOrders();
      } else {
        toast.error(data.error || 'Toplu silme başarısız');
      }
    } catch (error) {
      toast.error('Toplu silme başarısız');
    } finally {
      setBulkDeletingOrders(false);
    }
  };

  // ===== CUSTOMERS =====
  const fetchCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const response = await fetch(`${API_URL}/trendyol/customers-from-orders`);
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  // ===== LOGS =====
  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 100 });
      if (logFilter.type) params.set('type', logFilter.type);
      if (logFilter.status) params.set('status', logFilter.status);
      const response = await fetch(`${API_URL}/trendyol/sync-logs?${params}`);
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Loglar yüklenemedi:', error);
    } finally {
      setLoadingLogs(false);
    }
  }, [logFilter]);

  // Effects
  useEffect(() => {
    fetchStores();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'products') fetchProducts();
    else if (activeTab === 'orders') fetchOrders();
    else if (activeTab === 'customers') fetchCustomers();
    else if (activeTab === 'logs') fetchLogs();
  }, [activeTab, fetchProducts, fetchOrders, fetchCustomers, fetchLogs]);

  // Auto-sync polling
  useEffect(() => {
    if (!autoSyncEnabled || activeTab !== 'orders') return;

    ordersIntervalRef.current = setInterval(() => {
      fetchOrders();
      setLastOrderSync(new Date());
    }, ORDERS_POLL_INTERVAL);

    return () => {
      if (ordersIntervalRef.current) clearInterval(ordersIntervalRef.current);
    };
  }, [autoSyncEnabled, activeTab, fetchOrders]);

  useEffect(() => {
    if (!autoSyncEnabled || activeTab !== 'products') return;

    productsIntervalRef.current = setInterval(() => {
      fetchProducts();
      setLastProductSync(new Date());
    }, PRODUCTS_POLL_INTERVAL);

    return () => {
      if (productsIntervalRef.current) clearInterval(productsIntervalRef.current);
    };
  }, [autoSyncEnabled, activeTab, fetchProducts]);

  // Filter products
  const filteredProducts = products.filter(p => {
    if (!productFilter) return true;
    const q = productFilter.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) ||
           (p.barcode || '').toLowerCase().includes(q) ||
           (p.category || '').toLowerCase().includes(q);
  });

  // Filter orders
  const filteredOrders = orders.filter(o => {
    if (!orderSearchQuery) return true;
    const q = orderSearchQuery.toLowerCase();
    return (o.trendyol_order_number || '').toLowerCase().includes(q) ||
           (o.customer_name || '').toLowerCase().includes(q) ||
           (o.customer_email || '').toLowerCase().includes(q);
  });

  // Filter customers
  const filteredCustomers = customers.filter(c => {
    if (!customerSearchQuery) return true;
    const q = customerSearchQuery.toLowerCase();
    return (c.full_name || '').toLowerCase().includes(q) ||
           (c.email || '').toLowerCase().includes(q) ||
           (c.phone || '').includes(q);
  });

  const getSyncStatusBadge = (status) => {
    const map = {
      synced: { class: 'badge-success', text: 'Sync' },
      pending: { class: 'badge-warning', text: 'Bekliyor' },
      processing: { class: 'badge-info', text: 'İşleniyor' },
      error: { class: 'badge-error', text: 'Hata' },
      deleted: { class: 'badge-neutral', text: 'Silindi' },
      skipped: { class: 'badge-neutral', text: 'Atlandı' }
    };
    const item = map[status] || { class: 'badge-neutral', text: status || '-' };
    return <span className={`badge ${item.class}`}>{item.text}</span>;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('tr-TR');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(price || 0);
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) return `${API_URL.replace('/api', '')}${image}`;
    return `${API_URL.replace('/api', '')}/uploads/${image}`;
  };

  // ===== RENDER =====
  return (
    <div className="trendyol-container">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <ArrowLeft size={18} /> Geri
        </button>
        <h1>
          <ShoppingBag size={28} style={{ color: '#f97316' }} />
          Trendyol Entegrasyonu
        </h1>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="live-indicator">
            <span className={`live-dot ${autoSyncEnabled ? 'active' : 'inactive'}`} />
            <span style={{ color: autoSyncEnabled ? '#22c55e' : '#94a3b8' }}>
              {autoSyncEnabled ? 'CANLI' : 'DURDURULDU'}
            </span>
          </div>
          <button
            className={`btn btn-sm ${autoSyncEnabled ? 'btn-secondary' : 'btn-success'}`}
            onClick={() => setAutoSyncEnabled(!autoSyncEnabled)}
          >
            {autoSyncEnabled ? 'Durdur' : 'Başlat'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-navigation">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            {tab.name}
            {tab.id === 'orders' && newOrderCount > 0 && (
              <span className="badge badge-error" style={{ marginLeft: 6 }}>{newOrderCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="content-area">
        {/* ===== PRODUCTS TAB ===== */}
        {activeTab === 'products' && (
          <div>
            {/* Sync progress */}
            {syncProgress && syncProgress.running && (
              <div className="card" style={{ background: '#fffbeb' }}>
                <h3>Sync Durumu</h3>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${((syncProgress.success + syncProgress.failed) / syncProgress.total) * 100}%` }} />
                </div>
                <p style={{ fontSize: 14, color: '#92400e' }}>
                  {syncProgress.success + syncProgress.failed} / {syncProgress.total} ({syncProgress.success} başarılı, {syncProgress.failed} hata)
                </p>
              </div>
            )}

            {/* Toolbar */}
            <div className="toolbar">
              <input
                className="search-input"
                type="text"
                placeholder="Ürün ara (ad, barkod, kategori)..."
                value={productFilter}
                onChange={e => setProductFilter(e.target.value)}
              />
              <button className="btn btn-primary" onClick={syncAllProducts} disabled={syncingProducts}>
                <Upload size={16} />
                {syncingProducts ? 'Sync ediliyor...' : 'Tümünü Sync Et'}
              </button>
              <button className="btn btn-success" onClick={syncPrices} disabled={syncingPrices}>
                <Coins size={16} />
                {syncingPrices ? 'Güncelleniyor...' : 'Fiyat Sync'}
              </button>
              <button className="btn btn-secondary" onClick={syncInventory} disabled={syncingInventory}>
                <RefreshCw size={16} className={syncingInventory ? 'spin' : ''} />
                {syncingInventory ? 'Güncelleniyor...' : 'Stok Sync'}
              </button>
              {selectedProducts.length > 0 && (
                <button className="btn btn-danger btn-sm" onClick={() => showConfirmation({
                  title: 'Toplu Silme',
                  message: `${selectedProducts.length} ürünü Trendyol'dan kaldırmak istediğinize emin misiniz?`,
                  onConfirm: bulkDeleteProducts
                })} disabled={bulkDeleting}>
                  <Trash2 size={14} />
                  {bulkDeleting ? 'Siliniyor...' : `${selectedProducts.length} Ürün Sil`}
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={fetchProducts} disabled={loadingProducts}>
                <RefreshCw size={14} />
              </button>
            </div>

            {/* Products table */}
            <div className="card">
              {loadingProducts ? (
                <div className="empty-state">Yükleniyor...</div>
              ) : filteredProducts.length === 0 ? (
                <div className="empty-state">
                  <Package size={48} />
                  <p>E-ticaret aktif ürün bulunamadı</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedProducts(filteredProducts.map(p => p.id));
                            } else {
                              setSelectedProducts([]);
                            }
                          }}
                        />
                      </th>
                      <th>Resim</th>
                      <th>Ürün</th>
                      <th>Barkod</th>
                      <th>Kategori</th>
                      <th>Fiyat</th>
                      <th>Stok</th>
                      <th>Durum</th>
                      <th>Son Sync</th>
                      <th>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => (
                      <tr key={product.id}>
                        <td>
                          <input
                            type="checkbox"
                            className="checkbox"
                            checked={selectedProducts.includes(product.id)}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedProducts(prev => [...prev, product.id]);
                              } else {
                                setSelectedProducts(prev => prev.filter(id => id !== product.id));
                              }
                            }}
                          />
                        </td>
                        <td>
                          {product.image ? (
                            <img
                              className="product-image"
                              src={getImageUrl(product.image)}
                              alt={product.name}
                              onClick={() => setImagePopup({ show: true, src: getImageUrl(product.image), name: product.name })}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div style={{ width: 48, height: 48, background: '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Package size={20} color="#94a3b8" />
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: 600 }}>{product.name}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{product.barcode || '-'}</td>
                        <td>{product.category || '-'}</td>
                        <td>{formatPrice(product.sell_price)}</td>
                        <td>{product.total_stock || 0}</td>
                        <td>{getSyncStatusBadge(product.sync_status)}</td>
                        <td style={{ fontSize: 12 }}>{formatDate(product.last_synced_at)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-primary btn-sm" onClick={() => syncSingleProduct(product.id)} title="Sync Et">
                              <Upload size={12} />
                            </button>
                            {product.sync_status && (
                              <button className="btn btn-danger btn-sm" onClick={() => showConfirmation({
                                title: 'Ürünü Kaldır',
                                message: `"${product.name}" ürününü Trendyol'dan kaldırmak istediğinize emin misiniz?`,
                                onConfirm: () => deleteProduct(product.id)
                              })} title="Kaldır">
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ===== ORDERS TAB ===== */}
        {activeTab === 'orders' && (
          <div>
            <div className="toolbar">
              <input
                className="search-input"
                type="text"
                placeholder="Sipariş ara (numara, müşteri)..."
                value={orderSearchQuery}
                onChange={e => setOrderSearchQuery(e.target.value)}
              />
              <select
                style={{ padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10 }}
                value={orderFilter}
                onChange={e => setOrderFilter(e.target.value)}
              >
                <option value="all">Tümü</option>
                <option value="pending">Bekleyen</option>
                <option value="synced">İşlenmiş</option>
                <option value="error">Hatalı</option>
              </select>
              <button className="btn btn-primary" onClick={pullOrders} disabled={pullingOrders}>
                <Download size={16} />
                {pullingOrders ? 'Çekiliyor...' : 'Siparişleri Çek'}
              </button>
              <button className="btn btn-success" onClick={() => showConfirmation({
                title: 'Toplu İşleme',
                message: selectedOrders.length > 0
                  ? `${selectedOrders.length} siparişi işlemek istediğinize emin misiniz?`
                  : 'Tüm bekleyen siparişleri işlemek istediğinize emin misiniz?',
                onConfirm: bulkProcessOrders
              })} disabled={bulkProcessing}>
                <RefreshCw size={16} />
                {bulkProcessing ? 'İşleniyor...' : selectedOrders.length > 0 ? `${selectedOrders.length} Sipariş İşle` : 'Tümünü İşle'}
              </button>
              {(selectedOrders.length > 0 || orders.length > 0) && (
                <button className="btn btn-danger btn-sm" onClick={() => showConfirmation({
                  title: 'Sipariş Silme',
                  message: selectedOrders.length > 0
                    ? `${selectedOrders.length} siparişi silmek istediğinize emin misiniz?`
                    : 'Tüm siparişleri silmek istediğinize emin misiniz?',
                  onConfirm: bulkDeleteOrders
                })} disabled={bulkDeletingOrders}>
                  <Trash2 size={14} />
                  {bulkDeletingOrders ? 'Siliniyor...' : 'Sil'}
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={fetchOrders} disabled={loadingOrders}>
                <RefreshCw size={14} />
              </button>
            </div>

            <div className="card">
              {loadingOrders ? (
                <div className="empty-state">Yükleniyor...</div>
              ) : filteredOrders.length === 0 ? (
                <div className="empty-state">
                  <ShoppingBag size={48} />
                  <p>Sipariş bulunamadı</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedOrders(filteredOrders.map(o => o.id));
                            } else {
                              setSelectedOrders([]);
                            }
                          }}
                        />
                      </th>
                      <th>Sipariş No</th>
                      <th>Müşteri</th>
                      <th>Tutar</th>
                      <th>Durum</th>
                      <th>Kargo</th>
                      <th>Tarih</th>
                      <th>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map(order => (
                      <tr key={order.id}>
                        <td>
                          <input
                            type="checkbox"
                            className="checkbox"
                            checked={selectedOrders.includes(order.id)}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedOrders(prev => [...prev, order.id]);
                              } else {
                                setSelectedOrders(prev => prev.filter(id => id !== order.id));
                              }
                            }}
                          />
                        </td>
                        <td style={{ fontWeight: 600, cursor: 'pointer', color: '#f97316' }}
                            onClick={() => setSelectedOrder(order)}>
                          #{order.trendyol_order_number}
                        </td>
                        <td>
                          <div>{order.customer_name || '-'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{order.customer_phone || ''}</div>
                        </td>
                        <td style={{ fontWeight: 600 }}>{formatPrice(order.total_price)}</td>
                        <td>{getSyncStatusBadge(order.sync_status)}</td>
                        <td style={{ fontSize: 12 }}>{order.cargo_provider || '-'}</td>
                        <td style={{ fontSize: 12 }}>{formatDate(order.trendyol_created_at)}</td>
                        <td>
                          {order.sync_status !== 'synced' && (
                            <button className="btn btn-primary btn-sm" onClick={() => processOrder(order.id)}>
                              İşle
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ===== CUSTOMERS TAB ===== */}
        {activeTab === 'customers' && (
          <div>
            <div className="toolbar">
              <input
                className="search-input"
                type="text"
                placeholder="Müşteri ara..."
                value={customerSearchQuery}
                onChange={e => setCustomerSearchQuery(e.target.value)}
              />
              <button className="btn btn-secondary btn-sm" onClick={fetchCustomers} disabled={loadingCustomers}>
                <RefreshCw size={14} /> Yenile
              </button>
            </div>

            <div className="card">
              {loadingCustomers ? (
                <div className="empty-state">Yükleniyor...</div>
              ) : filteredCustomers.length === 0 ? (
                <div className="empty-state">
                  <Users size={48} />
                  <p>Müşteri bulunamadı</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Müşteri</th>
                      <th>Email</th>
                      <th>Telefon</th>
                      <th>Sipariş</th>
                      <th>Toplam Harcama</th>
                      <th>Son Sipariş</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map(customer => (
                      <tr key={customer.id}>
                        <td style={{ fontWeight: 600 }}>{customer.full_name}</td>
                        <td>{customer.email || '-'}</td>
                        <td>{customer.phone || '-'}</td>
                        <td>{customer.orders_count}</td>
                        <td style={{ fontWeight: 600 }}>{formatPrice(customer.total_spent)}</td>
                        <td style={{ fontSize: 12 }}>{formatDate(customer.last_order_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ===== SETTINGS TAB ===== */}
        {activeTab === 'settings' && (
          <div>
            {/* Connection Status */}
            <div className={`connection-status ${settings.api_key && settings.seller_id ? 'connected' : 'disconnected'}`}>
              {settings.api_key && settings.seller_id ? (
                <>
                  <span style={{ fontSize: 24 }}>&#10003;</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>Trendyol Bağlı</div>
                    <div style={{ fontSize: 12 }}>Satıcı ID: {settings.seller_id}</div>
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={() => showConfirmation({
                    title: 'Bağlantıyı Kes',
                    message: 'Trendyol bağlantısını kesmek istediğinize emin misiniz?',
                    onConfirm: disconnect
                  })} style={{ marginLeft: 'auto' }}>
                    Bağlantıyı Kes
                  </button>
                </>
              ) : (
                <>
                  <AlertTriangle size={24} color="#ef4444" />
                  <div>
                    <div style={{ fontWeight: 600 }}>Trendyol Bağlı Değil</div>
                    <div style={{ fontSize: 12 }}>API bilgilerini girerek bağlayın</div>
                  </div>
                </>
              )}
            </div>

            {/* API Settings */}
            <div className="card">
              <h3>API Ayarları</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Satıcı ID (Seller ID)</label>
                  <input
                    type="text"
                    value={settings.seller_id}
                    onChange={e => setSettings(prev => ({ ...prev, seller_id: e.target.value }))}
                    placeholder="154987"
                  />
                </div>
                <div className="form-group">
                  <label>Entegrasyon Referans Kodu</label>
                  <input
                    type="text"
                    value={settings.integration_code}
                    onChange={e => setSettings(prev => ({ ...prev, integration_code: e.target.value }))}
                    placeholder="c17cc17e-..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>API Key</label>
                  <input
                    type="password"
                    value={settings.api_key}
                    onChange={e => setSettings(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder="API Key"
                  />
                </div>
                <div className="form-group">
                  <label>API Secret</label>
                  <input
                    type="password"
                    value={settings.api_secret}
                    onChange={e => setSettings(prev => ({ ...prev, api_secret: e.target.value }))}
                    placeholder="API Secret"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Varsayılan Stok Düşüm Mağazası</label>
                  <select
                    value={settings.default_deduction_store_id}
                    onChange={e => setSettings(prev => ({ ...prev, default_deduction_store_id: e.target.value }))}
                  >
                    <option value="">Seçin...</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Komisyon Yüzdesi (%)</label>
                  <input
                    type="number"
                    value={settings.commission_percentage}
                    onChange={e => setSettings(prev => ({ ...prev, commission_percentage: parseFloat(e.target.value) || 0 }))}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fiyat Değişim Eşiği (%)</label>
                  <input
                    type="number"
                    value={settings.price_change_threshold}
                    onChange={e => setSettings(prev => ({ ...prev, price_change_threshold: parseFloat(e.target.value) || 2 }))}
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Otomatik Fiyat Sync Aralığı (dk, 0=kapalı)</label>
                  <input
                    type="number"
                    value={settings.price_sync_interval}
                    onChange={e => setSettings(prev => ({ ...prev, price_sync_interval: parseInt(e.target.value) || 0 }))}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="toggle-container">
                  <div
                    className={`toggle ${settings.sync_enabled ? 'active' : ''}`}
                    onClick={() => setSettings(prev => ({ ...prev, sync_enabled: !prev.sync_enabled }))}
                  />
                  <span style={{ fontWeight: 600 }}>Senkronizasyon Aktif</span>
                </div>
              </div>

              {/* Son sync bilgileri */}
              {(settings.last_synced_gold_price || settings.last_price_sync_at) && (
                <div style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
                  <div>Son sync altın: {settings.last_synced_gold_price ? `${parseFloat(settings.last_synced_gold_price).toFixed(2)} TL` : '-'}</div>
                  <div>Son sync USD: {settings.last_synced_usd_rate ? `${parseFloat(settings.last_synced_usd_rate).toFixed(4)}` : '-'}</div>
                  <div>Son fiyat sync: {formatDate(settings.last_price_sync_at)}</div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <button className="btn btn-primary" onClick={saveSettings} disabled={savingSettings}>
                  {savingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
                <button className="btn btn-secondary" onClick={testConnection} disabled={testingConnection}>
                  {testingConnection ? 'Test ediliyor...' : 'Bağlantıyı Test Et'}
                </button>
              </div>

              {connectionStatus && (
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 8,
                  background: connectionStatus.success ? '#dcfce7' : '#fecaca',
                  color: connectionStatus.success ? '#166534' : '#991b1b',
                  fontSize: 14
                }}>
                  {connectionStatus.message}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== LOGS TAB ===== */}
        {activeTab === 'logs' && (
          <div>
            <div className="toolbar">
              <select
                style={{ padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10 }}
                value={logFilter.type}
                onChange={e => setLogFilter(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="">Tüm Tipler</option>
                <option value="product_push">Ürün Push</option>
                <option value="product_update">Ürün Güncelleme</option>
                <option value="inventory_update">Stok Güncelleme</option>
                <option value="order_pull">Sipariş Çekme</option>
                <option value="price_update">Fiyat Güncelleme</option>
                <option value="product_delete">Ürün Silme</option>
              </select>
              <select
                style={{ padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10 }}
                value={logFilter.status}
                onChange={e => setLogFilter(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">Tüm Durumlar</option>
                <option value="success">Başarılı</option>
                <option value="error">Hata</option>
                <option value="warning">Uyarı</option>
              </select>
              <button className="btn btn-secondary btn-sm" onClick={fetchLogs} disabled={loadingLogs}>
                <RefreshCw size={14} /> Yenile
              </button>
            </div>

            <div className="card">
              {loadingLogs ? (
                <div className="empty-state">Yükleniyor...</div>
              ) : logs.length === 0 ? (
                <div className="empty-state">
                  <ClipboardList size={48} />
                  <p>Log bulunamadı</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Tip</th>
                      <th>Durum</th>
                      <th>Mesaj</th>
                      <th>Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>
                          <span className="badge badge-info">{log.sync_type}</span>
                        </td>
                        <td>{getSyncStatusBadge(log.status)}</td>
                        <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.message}
                        </td>
                        <td style={{ fontSize: 12 }}>{formatDate(log.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image Popup */}
      {imagePopup.show && (
        <div className="image-popup-overlay" onClick={() => setImagePopup({ show: false, src: '', name: '' })}>
          <div className="image-popup-content" onClick={e => e.stopPropagation()}>
            <img src={imagePopup.src} alt={imagePopup.name} />
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="order-detail-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="order-detail-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Sipariş #{selectedOrder.trendyol_order_number}</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelectedOrder(null)}>Kapat</button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div><strong>Müşteri:</strong> {selectedOrder.customer_name || '-'}</div>
              <div><strong>Telefon:</strong> {selectedOrder.customer_phone || '-'}</div>
              <div><strong>Email:</strong> {selectedOrder.customer_email || '-'}</div>
              <div><strong>Tutar:</strong> {formatPrice(selectedOrder.total_price)}</div>
              <div><strong>Durum:</strong> {getSyncStatusBadge(selectedOrder.sync_status)}</div>
              <div><strong>Kargo:</strong> {selectedOrder.cargo_provider || '-'} {selectedOrder.cargo_tracking_number ? `(${selectedOrder.cargo_tracking_number})` : ''}</div>
              <div><strong>Tarih:</strong> {formatDate(selectedOrder.trendyol_created_at)}</div>

              {selectedOrder.sync_error && (
                <div style={{ padding: 12, background: '#fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>
                  <strong>Hata:</strong> {selectedOrder.sync_error}
                </div>
              )}

              <div>
                <strong>Ürünler:</strong>
                <div style={{ marginTop: 8 }}>
                  {(() => {
                    try {
                      const items = typeof selectedOrder.order_items === 'string'
                        ? JSON.parse(selectedOrder.order_items)
                        : selectedOrder.order_items;
                      return (items || []).map((item, idx) => (
                        <div key={idx} style={{ padding: 8, background: '#f8fafc', borderRadius: 6, marginBottom: 4, fontSize: 13 }}>
                          {item.title || item.productName || item.barcode || 'Ürün'} x{item.quantity} - {formatPrice(item.price || item.amount)}
                        </div>
                      ));
                    } catch (e) {
                      return <div style={{ color: '#94a3b8' }}>Ürün detayı yüklenemedi</div>;
                    }
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>{confirmModalConfig.title}</h3>
            <p>{confirmModalConfig.message}</p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowConfirmModal(false)}>
                {confirmModalConfig.cancelText}
              </button>
              <button className="btn btn-danger" onClick={() => {
                confirmModalConfig.onConfirm();
                setShowConfirmModal(false);
              }}>
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendyolSettings;
